import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [total, setTotal]     = useState(0);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('');
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm]       = useState({ name:'', email:'', phone:'', gender:'male', fitness_goal:'general_fitness' });
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');

  const load = useCallback(() => {
    setLoading(true);
    axios.get('/admin/members', { params:{ search, status, page } })
      .then(r => { setMembers(r.data.members); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [search, status, page]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async e => {
    e.preventDefault(); setSaving(true); setMsg('');
    try {
      await axios.post('/admin/members', form);
      setMsg('Member added. Default password: fittofit123');
      setForm({ name:'', email:'', phone:'', gender:'male', fitness_goal:'general_fitness' });
      load();
    } catch (err) { setMsg(err.response?.data?.message || 'Error adding member.'); }
    finally { setSaving(false); }
  };

  const set = k => e => setForm(p => ({...p, [k]: e.target.value}));

  const daysLeft = expiry => {
    if (!expiry) return null;
    const diff = Math.ceil((new Date(expiry) - new Date()) / 86400000);
    return diff;
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Members</h1><p className="page-sub">{total} total registered</p></div>
        <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add member</button>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:10, marginBottom:18, flexWrap:'wrap' }}>
        <input className="input" placeholder="🔍 Search name, email, phone…" value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ flex:1, minWidth:200 }} />
        <select className="select" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }} style={{ width:150 }}>
          <option value="">All members</option>
          <option value="active">Active only</option>
          <option value="expired">Expired / No plan</option>
        </select>
      </div>

      <div className="card">
        {loading ? <div className="loading-wrap"><div className="spinner"></div></div> : (
          <table className="table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Phone</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Expiry</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 && <tr><td colSpan={6} style={{ textAlign:'center', padding:32, color:'var(--text-muted)' }}>No members found.</td></tr>}
              {members.map(m => {
                const days = daysLeft(m.expiry_date);
                return (
                  <tr key={m.id}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div className="avatar" style={{ width:32, height:32, fontSize:12 }}>
                          {m.photo ? <img src={m.photo} alt="" /> : m.name.slice(0,2).toUpperCase()}
                        </div>
                        <div>
                          <div className="name">{m.name}</div>
                          <div style={{ fontSize:11, color:'var(--text-muted)' }}>{m.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>{m.phone || '—'}</td>
                    <td>{m.plan_name || <span style={{ color:'var(--text-muted)' }}>No plan</span>}</td>
                    <td>
                      <span className={`badge ${m.membership_status === 'active' ? 'badge-success' : m.membership_status === 'suspended' ? 'badge-danger' : 'badge-muted'}`}>
                        {m.membership_status || 'no plan'}
                      </span>
                    </td>
                    <td>
                      {m.expiry_date ? (
                        <span style={{ fontSize:12, color: days < 7 ? 'var(--warning)' : 'var(--text-secondary)' }}>
                          {new Date(m.expiry_date).toLocaleDateString('en-GB')}
                          {days !== null && days >= 0 && <span style={{ fontSize:10, marginLeft:4 }}>({days}d left)</span>}
                          {days !== null && days < 0  && <span style={{ fontSize:10, marginLeft:4, color:'var(--danger)' }}>(expired)</span>}
                        </span>
                      ) : '—'}
                    </td>
                    <td><Link to={`/admin/members/${m.id}`} className="btn-ghost" style={{ fontSize:11, padding:'4px 10px' }}>View →</Link></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {total > 20 && (
          <div style={{ display:'flex', gap:8, padding:14, justifyContent:'center', borderTop:'1px solid var(--border)' }}>
            <button className="btn-ghost" disabled={page===1} onClick={() => setPage(p => p-1)}>← Prev</button>
            <span style={{ padding:'7px 12px', fontSize:13, color:'var(--text-secondary)' }}>Page {page} of {Math.ceil(total/20)}</span>
            <button className="btn-ghost" disabled={page >= Math.ceil(total/20)} onClick={() => setPage(p => p+1)}>Next →</button>
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      {showAdd && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.7)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div className="card" style={{ width:'100%', maxWidth:480, maxHeight:'90vh', overflow:'auto' }}>
            <div className="card-header">
              <div className="card-title">➕ Add new member</div>
              <button onClick={() => { setShowAdd(false); setMsg(''); }} style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:18 }}>✕</button>
            </div>
            <form onSubmit={handleAdd} style={{ padding:22 }}>
              {msg && <div style={{ background: msg.includes('Error') || msg.includes('already') ? 'var(--danger-dim)' : 'var(--success-dim)', border:`1px solid ${msg.includes('Error') || msg.includes('already') ? 'rgba(239,68,68,.3)' : 'rgba(34,197,94,.3)'}`, borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:16, fontSize:13, color: msg.includes('Error') || msg.includes('already') ? 'var(--danger)' : 'var(--success)' }}>{msg}</div>}
              <div className="form-group"><label className="label">Full name *</label><input className="input" value={form.name} onChange={set('name')} required /></div>
              <div className="form-row">
                <div className="form-group"><label className="label">Email *</label><input className="input" type="email" value={form.email} onChange={set('email')} required /></div>
                <div className="form-group"><label className="label">Phone</label><input className="input" value={form.phone} onChange={set('phone')} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="label">Gender</label>
                  <select className="select" value={form.gender} onChange={set('gender')}><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></select>
                </div>
                <div className="form-group"><label className="label">Fitness goal</label>
                  <select className="select" value={form.fitness_goal} onChange={set('fitness_goal')}>
                    <option value="weight_loss">Weight loss</option><option value="muscle_gain">Muscle gain</option>
                    <option value="endurance">Endurance</option><option value="general_fitness">General fitness</option>
                  </select>
                </div>
              </div>
              <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:16 }}>Default password will be <strong style={{ color:'var(--text-secondary)' }}>fittofit123</strong>. Member can change it after logging in.</p>
              <div style={{ display:'flex', gap:10 }}>
                <button className="btn-primary" type="submit" disabled={saving} style={{ flex:1, justifyContent:'center' }}>{saving ? 'Adding…' : 'Add member'}</button>
                <button type="button" className="btn-ghost" onClick={() => { setShowAdd(false); setMsg(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
